import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { X, ThumbsUp, Smile, ThumbsDown } from 'lucide-react';

const FeedbackPopup = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState(1);
  const [usefulness, setUsefulness] = useState('');
  const [mostValuable, setMostValuable] = useState('');
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleUsefulnessClick = (rating) => {
    setUsefulness(rating);
    
    // Track analytics
    if (window.posthog) {
      window.posthog.capture('demo_feedback_usefulness_selected', {
        rating: rating
      });
    }
    
    setStep(2);
  };

  const handleSubmit = () => {
    const feedbackData = {
      usefulness,
      mostValuable,
      feedback,
      email,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    };

    // Track analytics
    if (window.posthog) {
      window.posthog.capture('demo_feedback_submitted', {
        usefulness,
        most_valuable_feature: mostValuable,
        has_email: !!email
      });
    }

    onSubmit(feedbackData);
    setSubmitted(true);
  };

  const handleSkip = () => {
    // Track analytics
    if (window.posthog) {
      window.posthog.capture('demo_feedback_skipped', {
        step: step
      });
    }
    
    onClose();
  };

  const scrollToWaitlist = () => {
    onClose();
    document.getElementById('waitlist-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!isOpen) return null;

  return (
    <div className="feedback-overlay" onClick={onClose}>
      <Card className="feedback-popup" onClick={(e) => e.stopPropagation()}>
        <CardContent className="feedback-content">
          <button className="feedback-close" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>

          {!submitted ? (
            <>
              {step === 1 ? (
                <div className="feedback-step-1">
                  <h3 className="feedback-title">Welcome back — was the demo useful?</h3>
                  
                  <div className="usefulness-buttons">
                    <button
                      className="usefulness-btn very-useful"
                      onClick={() => handleUsefulnessClick('Very useful')}
                    >
                      <ThumbsUp className="usefulness-icon" />
                      <span>Very useful</span>
                    </button>
                    
                    <button
                      className="usefulness-btn somewhat-useful"
                      onClick={() => handleUsefulnessClick('Somewhat useful')}
                    >
                      <Smile className="usefulness-icon" />
                      <span>Somewhat useful</span>
                    </button>
                    
                    <button
                      className="usefulness-btn not-useful"
                      onClick={() => handleUsefulnessClick('Not useful')}
                    >
                      <ThumbsDown className="usefulness-icon" />
                      <span>Not useful</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="feedback-step-2">
                  <h3 className="feedback-title">Thanks — one quick follow-up</h3>
                  
                  <div className="feedback-form">
                    <div className="form-field">
                      <label className="field-label">Which feature felt most valuable?</label>
                      <select
                        className="field-select"
                        value={mostValuable}
                        onChange={(e) => setMostValuable(e.target.value)}
                      >
                        <option value="">Select a feature...</option>
                        <option value="Subscription detection">Subscription detection</option>
                        <option value="Trial-to-paid alerts">Trial-to-paid alerts</option>
                        <option value="AI-assisted subscription cancellation">AI-assisted subscription cancellation</option>
                        <option value="Credit score protection">Credit score protection</option>
                        <option value="Smart card guidance">Smart card guidance</option>
                        <option value="Balance rebalancing alerts">Balance rebalancing alerts</option>
                        <option value="Price increase alerts">Price increase alerts</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-field">
                      <label className="field-label">What would make this product a must-have for you?</label>
                      <textarea
                        className="field-textarea"
                        rows="4"
                        placeholder="Share your thoughts..."
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                      />
                    </div>

                    <div className="form-field">
                      <label className="field-label">Want early access when this launches? (Optional)</label>
                      <Input
                        type="email"
                        placeholder="Enter your email (optional)"
                        className="field-input"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="feedback-actions">
                      <Button
                        size="lg"
                        className="submit-btn"
                        onClick={handleSubmit}
                      >
                        Submit Feedback
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={handleSkip}
                      >
                        Skip
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="feedback-confirmation">
              <div className="confirmation-icon">✓</div>
              <h3 className="confirmation-title">Thanks for the feedback!</h3>
              <p className="confirmation-text">Want early access when we launch?</p>
              <Button
                size="lg"
                className="confirmation-btn"
                onClick={scrollToWaitlist}
              >
                Join Early Access
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FeedbackPopup;
